/**
 * Weighted (piecewise-linear) scale for resolution range sliders.
 *
 * A linear resolution slider wastes most of its travel on coarse values when the
 * interesting data sits at the fine (low-metre) end. This maps the slider's linear
 * track position to a non-linear metre value: the first `breakpointPct`% of the
 * track covers `[min, breakpoint]` (fine control) and the rest covers
 * `(breakpoint, max]` (coarse). The consumer still stores real metres — only the
 * thumb's position along the track is non-linear.
 *
 * When the range doesn't straddle the breakpoint (e.g. a product set whose values
 * are all below or all above it) the scale degrades to a plain linear mapping, so
 * it is safe to feed a dynamic `{ min, max }` derived from data.
 */

export const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

export type ResolutionScaleConfig = {
  /** Lower bound of the value range, in metres. */
  min: number;
  /** Upper bound of the value range, in metres. */
  max: number;
  /** Value (metres) below which the track is expanded for fine control. */
  breakpoint: number;
  /** Percentage of the track (0–100) devoted to the `[min, breakpoint]` band. */
  breakpointPct: number;
};

export type ResolutionScale = {
  /** Value in metres → slider track position (0–100). */
  toPosition: (metres: number) => number;
  /** Slider track position (0–100) → value in metres, rounded to 2dp. */
  toValue: (position: number) => number;
};

export const makeResolutionScale = ({
  min,
  max,
  breakpoint,
  breakpointPct,
}: ResolutionScaleConfig): ResolutionScale => {
  // Only apply the weighting when the range actually spans the breakpoint;
  // otherwise fall back to a linear scale (fineMax = max, whole track is "fine").
  const weighted = min < breakpoint && breakpoint < max;
  const pct = weighted ? breakpointPct : 100;
  const fineMax = weighted ? breakpoint : max;

  const toPosition = (metres: number): number => {
    const v = clamp(metres, min, max);
    if (v <= fineMax) {
      return fineMax === min ? 0 : ((v - min) / (fineMax - min)) * pct;
    }
    return pct + ((v - fineMax) / (max - fineMax)) * (100 - pct);
  };

  const toValue = (position: number): number => {
    const p = clamp(position, 0, 100);
    const metres =
      p <= pct
        ? min + (pct === 0 ? 0 : (p / pct) * (fineMax - min))
        : fineMax + ((p - pct) / (100 - pct)) * (max - fineMax);
    return Math.round(metres * 100) / 100; // 2dp keeps stored & displayed values clean
  };

  return { toPosition, toValue };
};
